import { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { dateInput } from 'components/final-form/DateInput';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import { radioGroupInput } from 'components/final-form/RadioGroupInput';
import Select from 'components/final-form/Select';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import { sortZipData } from 'components/patient/utils/patientUtils';
import React, { Component } from "react"
import { Field } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Grid, Header, Icon, Popup, RadioProps } from 'semantic-ui-react';
import axios from 'services/http';
import { searchZipDataForInput } from 'services/zipDataServices';
import styled from 'styled-components';
import { ZipDataOrderBy } from 'ts-types/api.enums';
import { UpsertPatientDto } from 'ts-types/api.types';
import { IndexSignature } from 'utils/praxTypes';
import { ahvValidator, required } from 'utils/validatorUtils';

const StyledColumn = styled(Grid.Column)`
  &.no-padding-top {
    padding-top: 0 !important;
  }
`;

const StyledHeader = styled(Header)`
  &.padding-top {
    padding-top: 7px;
  }

  &.mandate-header {
    margin-left: calc(-1rem - 5px);
  }
`;

const PopupContentDiv = styled.div`
  & {
    max-width: 71rem;
    white-space: pre-wrap;
  }
`;

export const renderProblematicPatient = (patient:UpsertPatientDto, message?: string) => {

  if (patient && patient.doctorProblematic) {
    return <>
      <span style={{color:"red"}}></span>
      <Popup
          trigger={<Icon name="exclamation circle" color="red"/>}
          content={<PopupContentDiv>{patient.remark}</PopupContentDiv>}
          on="hover"
          size="small"
          position="bottom center"
      />
      <span style={{color: "red"}}>{message && message}</span>
    </>;
  }

  return <></>;
}

interface Props extends MessageSourceProps {
  patient: UpsertPatientDto,
  initialCities: Array<DropdownOption>,
  initialZip: Array<DropdownOption>,
  countries: Array<DropdownOption>,
  genders: Array<RadioProps>,
  civilStatuses: Array<DropdownOption>,
  languages: Array<DropdownOption>,
  billTexts: Array<DropdownOption>,
  mandates: Array<DropdownOption>,
  countryId: number
}

interface State {
  cities: Array<DropdownOption>,
  zip: Array<DropdownOption>,
  cancelTokenSource: CancelTokenSource
}

class PatientPane extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {initialCities, initialZip} = this.props;

    this.state = {
      cities: initialCities,
      zip: initialZip,
      cancelTokenSource: axios.CancelToken.source()
    }
  }

  componentDidUpdate({initialCities: prevCities, initialZip: prevZip}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {

    const {initialCities, initialZip} = this.props;

    if (prevCities.length != initialCities.length || prevZip.length != initialZip.length) {
      this.setState({
        zip: initialZip,
        cities: initialCities
      })
    }
  }

  searchZipData = (query: string) => {

    const {countryId} = this.props;
    const {cities, zip: zips, cancelTokenSource} = this.state;

    searchZipDataForInput(query, countryId, cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          this.setState({
            cities: [...allInitialAndNewCityAdditions, ...this.mapZipData("city", response, ZipDataOrderBy.CITY)],
            zip: [...allInitialAndNewZipAdditions, ...this.mapZipData("zip", response, ZipDataOrderBy.ZIP)]
          })
        })
        .catch(() => {
          this.setState({
            cities: [],
            zip: []
          })
        })
  };

  mapZipData = (field: string, zipData: Array<IndexedZipData>, orderBy: ZipDataOrderBy): Array<DropdownOption> => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };

  onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    const {cities, zip: zips} = this.state;

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        this.setState({
          cities: [newAddition, ...newCities]
        });
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key == newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption === true);
        this.setState({
          zip: [newAddition, ...newZips]
        });
      }
    }
  };

  mapFieldRows = (fields: Array<any>, messageKey?: string) => {

    const {getMessage} = this.props;

    return fields.map((fieldProps, index) => {
      const labelKey = messageKey ? `${messageKey}.${fieldProps.name}` : fieldProps.name;
      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage(labelKey)}:
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });
  };

  validator = (value: string) => required(value);

  render(): React.ReactNode {
    return (
        <>
          {this.renderBasicInformationSection()}
          {this.renderDetailInformationSection()}
          {this.renderTelephoneInfoSection()}
          {this.renderMandateField()}
        </>
    )
  }

  renderBasicInformationSection = (): JSX.Element => {

    const {
      getMessage,
      genders,
      countries,
      patient
    } = this.props;

    const {cities, zip} = this.state;

    const selectMap: IndexSignature = {
      city: cities,
      zip: zip,
      countryId: countries
    };

    const basicInfoLeftFields = [
      radioGroupInput({name: "sex"}, genders),
      textInput({
        name: "title"
      }),
      textInput({
        name: "name",
        validate: this.validator
      }),
      textInput({
        name: "firstName",
        validate: this.validator
      }),
      textInput({
        name: "birthName"
      }),
      textInput({
        name: "position"
      })
    ];

    const basicInfoRightFields = [
      textInput({
        name: "email"
      }),
      textInput({
        name: "street",
        validate: this.validator
      }),
      selectInput({
            name: "zip",
            validate: this.validator,
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: (this.searchZipData),
            onNewAddition: (this.onNewAddition("zip"))
          },
          selectMap
      ),
      selectInput({
            name: "city",
            validate: this.validator,
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: (this.searchZipData),
            onNewAddition: (this.onNewAddition("cities"))
          },
          selectMap
      ),
      selectInput({
            name: "countryId",
            validate: this.validator
          },
          selectMap
      ),
      textInput({
        name: "passportNumber"
      })
    ];

    const mappedLeftRow = this.mapFieldRows(basicInfoLeftFields, "patient");
    const mappedRightRow = this.mapFieldRows(basicInfoRightFields);

    return (
        <PraxFormGrid stackable style={{paddingBottom: 20}}>
          <Grid.Row>
            <Grid.Column width={10} verticalAlign="middle">
              <StyledHeader>
                <div>
                  {getMessage("patient.basicInformation") + " "}
                  {renderProblematicPatient(patient)}
                </div>
              </StyledHeader>
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("Active Patient")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              {mappedLeftRow}
            </PraxFormGrid>
          </StyledColumn>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              {mappedRightRow}
            </PraxFormGrid>
          </StyledColumn>
        </PraxFormGrid>
    )
  };

  renderDetailInformationSection = (): React.ReactNode => {

    const {civilStatuses, languages, billTexts, getMessage} = this.props;

    const selectMap: IndexSignature = {
      civilStatus: civilStatuses,
      language: billTexts,
      languageId: languages
    };

    const detailInfoLeftFields = [
      dateInput({
        name: "birthDate",
        validate: this.validator
      }),
      dateInput({
        name: "dateOfDeath"
      }),
      selectInput({
            name: "civilStatus",
            validate: this.validator,
            upward: true
          },
          selectMap
      )
    ];

    const detailInfoRightFields = [
      textInput({
        name: "ahvNumber",
        validate: ahvValidator
      }),
      textInput({
        name: "externalIdManual"
      }),
      selectInput({
            name: "languageId",
            validate: this.validator,
            upward: true
          },
          selectMap
      ),
      selectInput({
            name: "language",
            validate: this.validator,
            upward: true
          },
          selectMap
      )
    ];

    const mappedLeftRow = this.mapFieldRows(detailInfoLeftFields, "patient");
    const mappedRightRow = this.mapFieldRows(detailInfoRightFields, "patient");

    return (
        <PraxFormGrid stackable style={{paddingBottom: 20}}>
          <Grid.Row>
            <Grid.Column>
              <StyledHeader className="padding-top">{getMessage("patient.detailInformation")}</StyledHeader>
            </Grid.Column>
          </Grid.Row>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              {mappedLeftRow}
            </PraxFormGrid>
          </StyledColumn>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              {mappedRightRow}
            </PraxFormGrid>
          </StyledColumn>
        </PraxFormGrid>
    );
  };

  renderTelephoneInfoSection = (): React.ReactNode => {

    const {getMessage} = this.props;

    const telInfoLeftFields = [
      textInput({
        name: "telephone"
      }),
      textInput({
        name: "mobilePhone"
      })
    ];

    const telInfoRightFields = [
      textInput({
        name: "telOffice"
      }),
      textInput({
        name: "fax"
      })
    ];

    const mappedLeftRow = this.mapFieldRows(telInfoLeftFields, "patient");
    const mappedRightRow = this.mapFieldRows(telInfoRightFields, "patient");

    return (
        <PraxFormGrid stackable>
          <Grid.Row>
            <Grid.Column>
              <StyledHeader>{`${getMessage("telephone")}/${getMessage("telefax")}`}</StyledHeader>
            </Grid.Column>
          </Grid.Row>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              {mappedLeftRow}
            </PraxFormGrid>
          </StyledColumn>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              {mappedRightRow}
            </PraxFormGrid>
          </StyledColumn>
        </PraxFormGrid>
    );
  }

  renderMandateField = (): React.ReactNode => {

    const {mandates, getMessage} = this.props;

    return (
        <PraxFormGrid stackable>
          <StyledColumn className="no-padding-top" width={8}>
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={5} verticalAlign="middle">
                  <StyledHeader className="mandate-header">{getMessage("mandate")}</StyledHeader>
                </Grid.Column>
                <Grid.Column width={11} verticalAlign="middle">
                  <Field
                      name="mandateId"
                      component={Select}
                      options={mandates}
                      upward
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </StyledColumn>
        </PraxFormGrid>
    );
  }

}

export default withMessages(PatientPane);
